import cx from 'classnames';
import { get, isNil } from 'lodash';
import { Component, ReactElement } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { compose } from 'recompose';
import { ELSFooter } from '@els/els-component-footer-react';
import { AssessmentRS, ChartFragmentsRS } from 'models/api-response';
import { ELSModalServiceType } from 'models/els';
import { AssessmentStatus, AssignmentType, EventType, FragmentType, HOSPITAL_FLOOR, LocationType, UserRole } from 'models/enum';
import { CaseStudySequence, NavigationItem, PatientContext, SimChartAssignment, SimulationEhrSequence, SkinnyAssignment, SkipLinkSection, TimeSpentPartial, User } from 'models/ui';
import { AppConstant, AuthoringFakeData, ExcludesSkipLinkPaths, ExternalLinks, Locales, NAV_ID, RouteParams, RoutePath, SkipLinkId } from 'constants/app.constant';
import { apiHelper, appHelper, assignmentHelper, cssHelper } from 'helpers';
import { assignmentService, caseStudyService, chartService, navigationService, simulationService } from 'services';
import { getInstructorDetails } from 'services/assignment.service';
import { appActions, appSelectors } from 'redux/ducks/app';
import { studentActions, studentSelectors } from 'redux/ducks/student';
import { Box, GradingChart, PageHeader } from 'components/common';
import { combineStudentInstructor } from 'components/common/chart-wrapper/helper';
import { ELSButton, ELSFlex, ELSFlexItem, ELSIcon, ELSWithModalService } from 'components/common/els';
import { AssignmentBar, Breadcrumb, Navigation, PatientRibbon } from 'components/common/layout';
import AssessmentSubmission from 'components/features/assessment-submission/AssessmentSubmission';
import './page.wrapper.scss';

interface MatchParams {
  assessmentId: string;
}

interface PageWrapperPropsOnly {
  children(arg: { loggedInUser: User; userRole: string; locale: string }): ReactElement;
  loggedInUser: User;
  userRole: string;
  showPatientRibbon: boolean;
  showNavigation: boolean;
  showChartingDateTime: boolean;
  menuItems: NavigationItem[];
  title: string;
  assignment: SkinnyAssignment;
  assessment: AssessmentRS;
  getMenuItems: (assessmentId: number, patientContext: PatientContext, skipCache?: boolean) => any;
  selectedNavId: string;
  isPlaceHolder: boolean;
  showSubmitAssessmentButton: boolean;
  modalService: ELSModalServiceType;
  isAuthor: boolean;
  phaseIndex: number;
  setSkipLinkSections: Function;
  setAssessment: Function;
  toggleFlyout: (content: JSX.Element | null) => void;
  setAssignment: Function;
  setAssignmentType: Function;
  setChartingTime: Function;
  fetchPatientContext: Function;
  setPatientContext: Function;
  setPhaseIndex: Function;
  setSelectedNavId: Function;
  setTimeSpentData: Function;
  locale: Locales;
  assignmentType: string;
  fullAssignment: SimChartAssignment;
  isInProgressGradingEnabledFlag: boolean;
  isInstructorSaveData?: boolean;
  isInstructorSaveDataUS?: boolean;
  dataAssessmentInstructor?: AssessmentRS & { userId: string; assignmentId?: number };
  loggedInUserId?: number;
  setDataAssessmentInstructor?: Function;
  isSimulationEHREnabled?: boolean;
}

interface PageWrapperState {
  openNavigation: boolean;
  isDataLoaded: boolean;
  filteredShownMenuItems: NavigationItem[];
  isInProgressGradingEnabled: boolean;
  isDemoMode: boolean;
}

export type PageWrapperProps = PageWrapperPropsOnly & RouteComponentProps<MatchParams>;

class PageWrapper extends Component<PageWrapperProps, PageWrapperState> {
  static displayName = 'PageWrapper';
  static defaultProps = {
    title: 'SimChart Assignment',
    showPatientRibbon: true,
    showNavigation: true,
    showSubmitAssessmentButton: true
  };

  constructor(props) {
    super(props);
    const shouldCollapseNavigation = appHelper.getWindowMode().mobile || appHelper.getWindowMode().tablet;
    this.state = {
      openNavigation: !shouldCollapseNavigation,
      isDataLoaded: false,
      filteredShownMenuItems: [],
      isInProgressGradingEnabled: false,
      isDemoMode: appHelper.isDemoMode()
    };
  }

  async componentDidMount() {
    const { location, setSelectedNavId, isInProgressGradingEnabledFlag } = this.props;
    this.sendCurrentPhaseIndex(Number(this.props.match.params.assessmentId));
    this.loadData();
    if (location.pathname.endsWith('summary')) {
      setSelectedNavId(NAV_ID.SUMMARY);
    }
    this.setState({ isInProgressGradingEnabled: isInProgressGradingEnabledFlag });
  }

  componentDidUpdate(prevProps: Readonly<PageWrapperProps>) {
    const { assignmentType, menuItems, locale, isSimulationEHREnabled } = this.props;
    const menuItemsChanged = menuItems !== prevProps.menuItems && menuItems;
    const featureFlagChanged = isSimulationEHREnabled !== prevProps.isSimulationEHREnabled;
    if (menuItemsChanged || (featureFlagChanged && menuItems)) {
      this.setState({ filteredShownMenuItems: navigationService.filterShownMenuItems(menuItems, this.getExcludedNavPathFragments()) });
    }

    // Reload data when locale changes.
    // This reload will fetch new patient context which contains new locale.
    // It should be done before fetchChartMetadata in withChartMetadata HOC.
    if (prevProps.locale !== locale) {
      this.loadData();
    }

    if (assignmentType !== prevProps.assignmentType) {
      this.sendCurrentPhaseIndex(Number(this.props.match.params.assessmentId));
    }
  }

  get isInstructor(): boolean {
    return this.props.userRole === UserRole.INSTRUCTOR;
  }

  getExcludedNavPathFragments = (): string[] => {
    const excludedPathFragments: string[] = [];
    const toNavPathFragment = (path: string): string => path.split('/patient-charting/')[1];
    // Neurological Evaluation, Annual Ambulatory Care, OB Triage and Informed Consent are US-only charts gated behind the SIMULATION_EHR flag; hide for ANZ or when the flag is off.
    if (!this.props.isSimulationEHREnabled || this.props.locale === Locales.EN_AU) {
      excludedPathFragments.push(toNavPathFragment(RoutePath.student.specialCharts.neurologicalEvaluation));
      excludedPathFragments.push(toNavPathFragment(RoutePath.student.specialCharts.annualAmbulatoryCare));
      excludedPathFragments.push(toNavPathFragment(RoutePath.student.specialCharts.obTriage));
      excludedPathFragments.push(toNavPathFragment(RoutePath.student.specialCharts.informedConsent));
    }
    return excludedPathFragments;
  };

  // Only sets partial data needed for sendTimeSpent api call
  setChartTimeSpentData = (): TimeSpentPartial => {
    const { assignment, phaseIndex, selectedNavId, setTimeSpentData } = this.props;
    // Set to 0 when assignmentId shows undef/null on pages like Instructor demo
    const assignmentId = isNil(assignment?.assignmentId) ? 0 : assignment.assignmentId;
    const timeSpentPartialData: TimeSpentPartial = {
      assignmentId: isNil(assignmentId) ? 0 : assignmentId,
      assessmentId: this.props.assessment.eolsAssessmentId,
      userId: this.props.loggedInUser.id,
      phaseIndex,
      currentLocation: {
        id: selectedNavId,
        type: LocationType.PHASE_CHART
      }
    };
    setTimeSpentData(timeSpentPartialData);
    // Quick return for START calls made in PageWrapper
    return timeSpentPartialData;
  };

  loadData = () => {
    const { location, isAuthor, getMenuItems, setAssessment, setAssignmentType, setPatientContext, setPhaseIndex, setSkipLinkSections, locale } = this.props;
    const { assessmentId } = this.props.match.params;
    let promise;

    /**
     * if user is an author
     * just get menu items
     * if the user isn't an author and the assessmentId is defined
     * get assessment and menu items
     */

    if (isAuthor) {
      const hospitalFloors = [HOSPITAL_FLOOR.MEDSURG, HOSPITAL_FLOOR.PEDIATRIC, HOSPITAL_FLOOR.PSYCH, HOSPITAL_FLOOR.WOMAN_HEALTH];
      const authorPatientContext: PatientContext = {
        assignmentType: AssignmentType.CASE_STUDY,
        gender: 'MALE',
        ageInDays: null,
        hospitalFloors,
        isAuthor,
        locale
      };
      // Skip cache for authoring.
      promise = getMenuItems(Number(AuthoringFakeData.assessmentId), authorPatientContext, true);
      setAssessment(AuthoringFakeData.assessment);
      setAssignmentType(AssignmentType.CASE_STUDY);
      setPatientContext(authorPatientContext);
    } else if (assessmentId) {
      const sections: SkipLinkSection[] = [];
      const searchPath = location.pathname.replace(String(assessmentId), RouteParams.ASSESSMENT_ID);
      if (!ExcludesSkipLinkPaths.includes(searchPath)) {
        sections.push(SkipLinkId.navigation, SkipLinkId.main, SkipLinkId.footer);
      }
      setSkipLinkSections(sections);

      promise = this.fetchAssessment(Number(assessmentId)).then(async (data) => {
        const isEmptyEhr = data.assignmentType === AssignmentType.EMPTY_EHR;
        if (isEmptyEhr && !isAuthor) {
          setPhaseIndex(0);
          assignmentHelper.recordTimeSpent(this.setChartTimeSpentData(), EventType.START);
        }

        // The charts show the instructor clinical setup when it is newer, so the menu has to be filtered by the same patient.
        const instructorChartId = this.shouldMergeInstructorData(data.studentId) ? await this.getInstructorChartId(data.eolsAssignmentId) : null;
        const patientContext = await this.props.fetchPatientContext(data.simChartId, data.assignmentType, false, instructorChartId);
        if (patientContext) {
          return getMenuItems(Number(assessmentId), patientContext);
        }
        return [];
      });
    }
    return Promise.resolve(promise).then(() => {
      this.setState({ isDataLoaded: true });
    });
  };

  shouldMergeInstructorData = (studentId: number): boolean => {
    const { isInstructorSaveData, isInstructorSaveDataUS, locale, userRole, loggedInUserId } = this.props;
    const instructorAccessSelfCharting = studentId === loggedInUserId && userRole === UserRole.INSTRUCTOR;
    const isSaveDataEnabled = (locale === Locales.EN_AU && isInstructorSaveData) || (locale === Locales.EN_US && isInstructorSaveDataUS);
    return isSaveDataEnabled && !instructorAccessSelfCharting;
  };

  loadChartData = async (fragmentTypes = [FragmentType.AUTHORED, FragmentType.CHARTING, FragmentType.STATUS], navIds = [], chartId = null): Promise<ChartFragmentsRS> => {
    const { assessment, selectedNavId } = this.props;

    const loadedNavIds = navIds?.length > 0 ? navIds : [selectedNavId];
    const loadedChartId = chartId || assessment?.simChartId;
    const shouldFetchInstructor = this.shouldMergeInstructorData(assessment?.studentId);

    const studentPromise: Promise<ChartFragmentsRS> = chartService.loadFragments({ chartId: loadedChartId, navIds: loadedNavIds, fragmentTypes }).catch(() => ({ data: [] }));

    const instructorPromise: Promise<ChartFragmentsRS> = shouldFetchInstructor ? this.loadInstructorFragments(loadedNavIds, fragmentTypes) : Promise.resolve({ data: [] });

    const [studentRes, instructorRes] = await Promise.all([studentPromise, instructorPromise]);
    const student = (studentRes?.data ?? []).map((item) => ({ ...item, __source: 'student' }));
    const instructor = (instructorRes?.data ?? []).map((item) => ({ ...item, __source: 'instructor' }));

    const mergeNavElementIds = [NAV_ID.PATIENT];
    const finalData = combineStudentInstructor(student, instructor, mergeNavElementIds);
    return { data: finalData };
  };

  loadInstructorFragments = async (navIds: string[], fragmentTypes: FragmentType[]): Promise<ChartFragmentsRS> => {
    const instructorChartId = await this.getInstructorChartId();
    if (!instructorChartId) return { data: [] };
    return chartService.loadFragments({ chartId: instructorChartId, navIds, fragmentTypes }).catch(() => ({ data: [] }));
  };

  getInstructorChartId = async (eolsAssignmentId = this.props.assessment?.eolsAssignmentId): Promise<string | null> => {
    const { dataAssessmentInstructor, loggedInUserId, setDataAssessmentInstructor } = this.props;

    // The cached instructor data is kept across assignments, so only reuse it for the same assignment.
    const cachedId = dataAssessmentInstructor?.assignmentId === eolsAssignmentId ? dataAssessmentInstructor?.simChartId : null;
    if (cachedId) return cachedId;

    try {
      const { data } = await getInstructorDetails(eolsAssignmentId, loggedInUserId);
      if (data) {
        setDataAssessmentInstructor(data);
      }
      return data?.simChartId ?? null;
    } catch (err) {
      console.error('getInstructorChartId fetch failed', err);
      return null;
    }
  };

  sendCurrentPhaseIndex = (assessmentId: number) => {
    const { assignmentType, isAuthor, setPhaseIndex } = this.props;
    const handleSequenceChunk = (data: { phaseId: string; displayOrder: number }) => {
      localStorage.setItem(AppConstant.PHASE_ID, data?.phaseId);
      const currentPhaseIndex = data.displayOrder > 0 ? data.displayOrder : 0;
      setPhaseIndex(currentPhaseIndex);

      if (!isAuthor) {
        assignmentHelper.recordTimeSpent(this.setChartTimeSpentData(), EventType.START);
      }
    };

    if (assignmentType === AssignmentType.CASE_STUDY) {
      caseStudyService.getCurrentOrFirstSequenceChunk(assessmentId).then(({ data }: { data: CaseStudySequence }) => handleSequenceChunk(data));
    } else if (assignmentType === AssignmentType.SIMULATION) {
      simulationService.getCurrentOrFirstSequenceChunk(assessmentId).then(({ data }: { data: SimulationEhrSequence }) => handleSequenceChunk(data));
    }
  };

  fetchAssessment = (assessmentId: number) => {
    const { assignment, setAssessment, setAssignment, setAssignmentType } = this.props;

    return apiHelper.withApiErrorHandler(async () => {
      const assessmentResponse = await assignmentService.getAssessment(assessmentId);
      const assessmentData = get(assessmentResponse, 'data', assessmentResponse);

      if (assignment?.assignmentId !== assessmentData.eolsAssignmentId) {
        const assignmentResponse = await assignmentService.getSimChartAssignment(assessmentData.eolsAssignmentId);
        setAssignment({ ...assignmentResponse.data, assignmentId: assignmentResponse.data.eolsAssignmentId });
      }

      setAssessment({ ...assessmentData, assignmentId: assessmentData.eolsAssignmentId });
      setAssignmentType(assessmentData.assignmentType);

      return {
        data: {
          assignmentType: assessmentData.assignmentType,
          simChartId: assessmentData.simChartId,
          eolsAssignmentId: assessmentData.eolsAssignmentId,
          studentId: assessmentData.studentId
        }
      };
    }, null);
  };

  toggleNavigation = () => this.setState((prevState) => ({ openNavigation: !prevState.openNavigation }));

  renderSubmitButton = () => {
    const { showSubmitAssessmentButton, assessment } = this.props;
    const isAssessmentGradable = assignmentHelper.isAssessmentSubmitted(assessment?.status);

    if (!showSubmitAssessmentButton || isAssessmentGradable || this.isInstructor) return null;

    return (
      <Box pr className="o-els-flex-layout__item">
        <AssessmentSubmission assessmentId={assessment?.eolsAssessmentId} />
      </Box>
    );
  };

  renderBreadcrumbBar = () => {
    const { showNavigation } = this.props;
    const { openNavigation, filteredShownMenuItems } = this.state;

    if (!showNavigation) return null;

    return (
      <div className="page-wrapper__breadcrumbs-bar">
        <ELSButton id="nav-toggle" onClick={this.toggleNavigation} type="debuttonize" ariaLabel="Collapse/Expand Navigation" ariaExpanded={openNavigation}>
          {openNavigation && <ELSIcon name="nav-collapse" prefix="hmds" color="n9" />}
          {!openNavigation && <ELSIcon name="nav-expand" prefix="hmds" color="n9" />}
        </ELSButton>
        <Breadcrumb items={filteredShownMenuItems} />
      </div>
    );
  };

  renderMainContent = () => {
    const { showNavigation, selectedNavId, isPlaceHolder, loggedInUser, userRole, locale } = this.props;
    const { filteredShownMenuItems } = this.state;

    return (
      <ELSFlexItem grow className={cx('page-wrapper__content-wrapper', { 'u-els-margin-top-2x': !showNavigation })}>
        <div className={cssHelper.mapElsCss({ els: 'padding-left-2x padding-right-2x', md: 'padding-left-1x1o2 padding-right-1x1o2', lg: 'padding-left-2x padding-right-2x' })}>
          {this.renderBreadcrumbBar()}
          <div className="page-wrapper__children-wrapper" id="children-wrapper">
            {(selectedNavId || isPlaceHolder) && this.props.children({ loggedInUser, userRole, locale })}
          </div>
          <Box mt2>
            <ELSFooter additionalLinks={[{ href: ExternalLinks.ACCESSIBILITY, text: 'Accessibility', target: '_blank' }]} />
          </Box>
        </div>
      </ELSFlexItem>
    );
  };

  renderGradingChart = () => {
    const { isAuthor, assessment } = this.props;
    const { isDemoMode, isInProgressGradingEnabled } = this.state;

    const isAssessmentGradable = assignmentHelper.isAssessmentSubmitted(assessment?.status);
    const isAssessmentStarted = assessment?.status !== AssessmentStatus.NOT_STARTED;
    const shouldShowGradingChart = !isAuthor && ((isInProgressGradingEnabled && isAssessmentStarted && !isDemoMode) || isAssessmentGradable);

    if (!shouldShowGradingChart) return null;

    return <GradingChart isInProgressGradingEnabled={isInProgressGradingEnabled} />;
  };

  render() {
    if (!this.state.isDataLoaded) return null;

    const { showChartingDateTime, showPatientRibbon, showNavigation, isAuthor } = this.props;
    const { openNavigation, filteredShownMenuItems } = this.state;

    return (
      <div className="page-wrapper">
        <PageHeader title={this.props.title} showWarningOnClose>
          {this.renderSubmitButton()}
        </PageHeader>

        {showChartingDateTime && (
          <div className="page-wrapper__assignment-bar-wrapper">
            <AssignmentBar toggleFlyout={this.props.toggleFlyout} />
          </div>
        )}

        {!isAuthor && showPatientRibbon && <PatientRibbon loadChartData={this.loadChartData} />}

        <ELSFlex className="page-wrapper__nav-and-content">
          <ELSFlexItem className="u-els-background-color-n0">
            {!showNavigation && <div className="nav--empty" />}
            {showNavigation && filteredShownMenuItems?.length > 0 && <Navigation show={openNavigation} items={filteredShownMenuItems} />}
          </ELSFlexItem>
          {this.renderMainContent()}
        </ELSFlex>

        {this.renderGradingChart()}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  getMenuItems: (assessmentId: number, patientContext: PatientContext, skipCache?: boolean) => dispatch(appActions.fetchMenuItems(assessmentId, patientContext, skipCache)),
  setAssessment: (assessment) => dispatch(appActions.setAssessment(assessment)),
  setAssignment: (assignment) => dispatch(studentActions.setAssignment(assignment)),
  setAssignmentType: (assignmentType) => dispatch(studentActions.setAssignmentType(assignmentType)),
  setChartingTime: (dateTime) => dispatch(studentActions.setChartingTime(dateTime)),
  setPatientContext: (patientContext: PatientContext) => dispatch(appActions.setPatientContext(patientContext)),
  fetchPatientContext: (simChartAssessmentId, assigmentType: AssignmentType, skipCache?: boolean, instructorChartId?: string) =>
    dispatch(appActions.fetchPatientContext(simChartAssessmentId, assigmentType, skipCache, instructorChartId)),
  setSkipLinkSections: (skipLinkSections: SkipLinkSection[]) => dispatch(appActions.setSkipLinkSections(skipLinkSections)),
  setPhaseIndex: (phaseIndex) => dispatch(studentActions.setPhaseIndex(phaseIndex)),
  setSelectedNavId: (navId) => dispatch(appActions.setSelectedNavId(navId)),
  setTimeSpentData: (timeSpentData) => dispatch(appActions.setTimeSpentData(timeSpentData)),
  setDataAssessmentInstructor: (data) => dispatch(appActions.setDataAssessmentInstructor(data))
});

const mapStateToProps = (state) => ({
  loggedInUser: appSelectors.getLoggedInUser(state),
  userRole: appSelectors.getUserRole(state),
  menuItems: appSelectors.getMenuItems(state),
  assignment: studentSelectors.getAssignment(state),
  assessment: appSelectors.getAssessment(state),
  selectedNavId: appSelectors.getNavId(state),
  isAuthor: appSelectors.getIsAuthor(state),
  phaseIndex: studentSelectors.getPhaseIndex(state),
  locale: appSelectors.getLocale(state),
  assignmentType: studentSelectors.getAssignmentType(state),
  fullAssignment: studentSelectors.getFullAssignment(state),
  isInProgressGradingEnabledFlag: appSelectors.getIsInProgressGradingEnabledFlag(state),
  dataAssessmentInstructor: appSelectors.getDataInstructorDetail(state),
  isInstructorSaveData: appSelectors.getInstructorSaveData(state),
  isInstructorSaveDataUS: appSelectors.getInstructorSaveDataUS(state),
  loggedInUserId: appSelectors.getUserId(state),
  isSimulationEHREnabled: appSelectors.getIsSimulationEHREnabled(state)
});

const enhancers = [withRouter, ELSWithModalService, connect(mapStateToProps, mapDispatchToProps)];
export { PageWrapper as BasePageWrapper };
export default compose(...enhancers)(PageWrapper);
